import { useState } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type TagProps = {
  children?: ReactNode;
  icon?: IconName | string;
  selected?: boolean;
  onRemove?: (e: MouseEvent) => void;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  style?: CSSProperties;
};

/**
 * Tag / Chip — selectable or removable label. Used here for the add-sheet's
 * type picker (授乳 / おむつ / ねんね / 離乳食) and the feed source picker.
 */
export function Tag({ children, icon, selected = false, onRemove, onClick, disabled = false, style }: TagProps) {
  const [hover, setHover] = useState(false);
  const clickable = !!onClick && !disabled;

  const styles: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    height: '32px',
    padding: '0 var(--space-3)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--fw-semibold)' as unknown as number,
    lineHeight: 1,
    borderRadius: 'var(--radius-pill)',
    border: selected ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
    background: selected ? 'var(--brand-subtle)' : 'var(--surface)',
    color: selected ? 'var(--brand)' : 'var(--text-muted)',
    cursor: clickable ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition:
      'background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)',
    ...(clickable && hover && !selected ? { background: 'var(--surface-sunken)' } : null),
    ...style,
  };

  return (
    <span
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? selected : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(e as unknown as MouseEvent);
              }
            }
          : undefined
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={styles}
    >
      {icon ? <Icon name={icon} size={15} /> : null}
      {children}
      {onRemove ? (
        <span
          role="button"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onRemove(e);
          }}
          style={{ display: 'inline-flex', marginRight: -4, opacity: 0.7, cursor: 'pointer' }}
        >
          <Icon name="X" size={14} />
        </span>
      ) : null}
    </span>
  );
}
